import { Flag } from 'lucide-react';
import { type CSSProperties, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { useReducedMotion } from '../lib/appearance';
import {
  type CairnStone,
  cairnExtent,
  cairnFit,
  cairnSlots,
  drop,
  dropKeyframes,
  ladder,
  type Mineral,
  type Silhouette,
  type Slot,
  silhouettePath,
  speckles,
  specular,
} from '../lib/stones';

/**
 * The drawings of the playful layer, as the phone draws them (`Stone.swift`,
 * `CairnView.swift`): a finished stone, a planned one, one held while the day
 * is chosen, the pebble that completes a task, and the cairn they stack into.
 * Colours come from the mineral tokens in `design-system.css`; every drawing
 * is decorative, so the words live on whatever holds it. A stone fills its
 * box, which is 100 × 64 everywhere but on a very tall cairn, where it is
 * drawn flatter.
 */

function useGradientId() {
  return `g${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

/** A finished stone: body, rim, grains and glint, lit from the top left. */
export function StoneMark({
  mineral,
  silhouette,
  speckled = true,
  className,
}: {
  mineral: Mineral;
  silhouette: Silhouette;
  speckled?: boolean;
  className?: string;
}) {
  const id = useGradientId();
  const glint = specular[silhouette];

  return (
    <svg
      className={className ? `stone ${className}` : 'stone'}
      data-mineral={mineral}
      viewBox="0 0 100 64"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={id} gradientUnits="userSpaceOnUse" cx="36" cy="19.2" r="82">
          <stop offset="0" className="stone-light" />
          <stop offset="0.5" className="stone-body" />
          <stop offset="1" className="stone-deep" />
        </radialGradient>
      </defs>
      <path className="stone-face" d={silhouettePath(silhouette)} fill={`url(#${id})`} />
      {speckled &&
        speckles[silhouette].map(([cx, cy, r]) => (
          <circle key={`${cx}-${cy}`} className="stone-grain" cx={cx} cy={cy} r={r} />
        ))}
      <ellipse
        className="stone-glint"
        cx={glint.cx}
        cy={glint.cy}
        rx={glint.rx}
        ry={glint.ry}
        transform={`rotate(-8 ${glint.cx} ${glint.cy})`}
      />
    </svg>
  );
}

/** A stone the day has room for but that is not placed yet: a dashed outline. */
export function GhostStone({
  silhouette,
  className,
}: {
  silhouette: Silhouette;
  className?: string;
}) {
  return (
    <svg
      className={className ? `stone stone-ghost ${className}` : 'stone stone-ghost'}
      viewBox="0 0 100 64"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={silhouettePath(silhouette)} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Chosen but not placed: washed in its mineral and outlined in a dashed line of it. */
export function HeldStone({ mineral, silhouette }: { mineral: Mineral; silhouette: Silhouette }) {
  return (
    <svg
      className="stone stone-held"
      data-mineral={mineral}
      viewBox="0 0 100 64"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={silhouettePath(silhouette)} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export type PebbleFace = 'open' | 'done' | 'setDown';

/**
 * The completion control's face: open, an outline washed in the task's
 * mineral; done, the stone the cairn receives with a tick on it; set down,
 * grey with a dash.
 */
export function PebbleMark({
  mineral,
  silhouette,
  face,
}: {
  mineral: Mineral;
  silhouette: Silhouette;
  face: PebbleFace;
}) {
  if (face === 'done') {
    return (
      <span className="pebble-mark pebble-done">
        <StoneMark mineral={mineral} silhouette={silhouette} speckled={false} />
        <svg className="pebble-tick" viewBox="0 0 100 64" aria-hidden="true">
          <path d="M33 34 L44 44.5 L66 23" />
        </svg>
      </span>
    );
  }

  return (
    <svg
      className={`stone pebble-mark pebble-${face === 'open' ? 'open' : 'set-down'}`}
      data-mineral={face === 'open' ? mineral : 'granite'}
      viewBox="0 0 100 64"
      aria-hidden="true"
    >
      <path d={silhouettePath(silhouette)} vectorEffect="non-scaling-stroke" />
      {face === 'setDown' && <path className="pebble-dash" d="M36 34 L64 34" />}
    </svg>
  );
}

/** A four-pointed star with pinched sides, in a 20 × 20 box. */
const sparkle = 'M10 0 Q11.5 8.5 20 10 Q11.5 11.5 10 20 Q8.5 11.5 0 10 Q8.5 8.5 10 0 Z';

/** Three sparkles over a capstone, revealed once it has landed. */
export function Sparkles({ scale = 1, delay = 0 }: { scale?: number; delay?: number }) {
  const stars: [number, number, number, string][] = [
    [-26, 6, 14, 'amber'],
    [28, -4, 10, 'amber'],
    [4, -18, 8, 'white'],
  ];

  return (
    <span
      className="sparkles"
      style={{ '--reveal': `${delay}ms` } as CSSProperties}
      aria-hidden="true"
    >
      {stars.map(([x, y, size, colour], index) => (
        <svg
          key={`${x}-${y}`}
          className={`sparkle sparkle-${index + 1} sparkle-${colour}`}
          viewBox="0 0 20 20"
          aria-hidden="true"
          style={{
            width: size * scale,
            height: size * scale,
            transform: `translate(calc(-50% + ${x * scale}px), calc(-50% + ${y * scale}px))`,
          }}
        >
          <path d={sparkle} />
        </svg>
      ))}
    </span>
  );
}

const capstoneId = 'capstone';

/**
 * A stone at its slot. The first time the cairn draws it, it falls: the
 * phone's `DropCurve`, sampled and played linearly, with a puff of dust where
 * it lands. Under Reduce Motion it fades in instead.
 */
function FallingStone({
  slot,
  fresh,
  delay,
  children,
}: {
  slot: Slot;
  fresh: boolean;
  delay: number;
  children: React.ReactNode;
}) {
  const element = useRef<HTMLSpanElement>(null);
  const reduced = useReducedMotion();
  const [puff, setPuff] = useState(false);

  // Decided once, when the stone first appears: a stone that has landed must
  // not fall again because the list around it redrew.
  const falling = useRef(fresh);

  useLayoutEffect(() => {
    if (!falling.current || !element.current) {
      return;
    }

    falling.current = false;

    if (reduced) {
      element.current.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 250,
        delay,
        easing: 'ease-out',
        fill: 'backwards',
      });

      return;
    }

    element.current.animate(dropKeyframes(slot.rotation), {
      duration: drop.duration,
      delay,
      easing: 'linear',
      fill: 'backwards',
    });

    const timer = setTimeout(() => setPuff(true), delay + drop.landing - 20);

    return () => clearTimeout(timer);
  }, [delay, reduced, slot.rotation]);

  return (
    <span className="cairn-slot" style={slotStyle(slot)}>
      <span
        ref={element}
        className="cairn-stone"
        style={{ transform: `rotate(${slot.rotation}deg)` }}
      >
        {children}
      </span>
      {puff && <DustPuff onDone={() => setPuff(false)} />}
    </span>
  );
}

/** Four grains kicked sideways when a stone lands. */
function DustPuff({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 650);

    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <span className="dust-puff" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

const slotStyle = (slot: Slot): CSSProperties => ({
  left: slot.x,
  top: slot.y,
  width: slot.width,
  height: slot.height,
});

/**
 * The day's cairn: placed stones, stones chosen and still in hand, the room
 * left for planned ones, and a capstone when everything committed to is done.
 *
 * A stone that was not on the cairn the last time it was drawn falls onto it.
 * That is decided here, by remembering what has already landed, so a stone
 * finished on another device drops in when sync brings it, as on the phone.
 */
export function CairnStack({
  placed,
  held = [],
  planned = [],
  capstone = false,
  scale = 1,
  className,
}: {
  placed: CairnStone[];
  held?: CairnStone[];
  planned?: Silhouette[];
  capstone?: boolean;
  scale?: number;
  className?: string;
}) {
  const landed = useRef<Set<string> | null>(null);

  if (!landed.current) {
    landed.current = new Set([...placed.map(s => s.id), ...(capstone ? [capstoneId] : [])]);
  }

  const silhouettes: Silhouette[] = [
    ...placed.map(s => s.silhouette),
    ...held.map(s => s.silhouette),
    ...planned,
    ...(capstone ? (['f'] as Silhouette[]) : []),
  ];

  const size = cairnExtent(silhouettes.length + (capstone ? 0 : 1), scale);
  const ground = size.height - 10 * scale;
  const slots = cairnSlots(silhouettes, size.width / 2, ground, scale);
  const base = ladder[0] * cairnFit(silhouettes, scale).across * 1.05;
  const firstGhost = placed.length + held.length;
  const capstoneFresh = capstone && !landed.current.has(capstoneId);

  // What stands on the stack now. A stone taken off by reopening its task
  // falls again when it is placed again.
  useEffect(() => {
    landed.current = new Set([...placed.map(s => s.id), ...(capstone ? [capstoneId] : [])]);
  });

  return (
    <div
      className={className ? `cairn ${className}` : 'cairn'}
      style={{ width: size.width, height: size.height }}
      aria-hidden="true"
    >
      <span
        className="cairn-shadow"
        style={{
          left: size.width / 2 - base / 2,
          top: ground + 2 * scale - 4 * scale,
          width: base,
          height: 8 * scale,
        }}
      />
      {placed.map((stone, index) => (
        <FallingStone
          key={stone.id}
          slot={slots[index]}
          fresh={!landed.current?.has(stone.id)}
          delay={0}
        >
          <StoneMark mineral={stone.mineral} silhouette={stone.silhouette} />
        </FallingStone>
      ))}
      {held.map((stone, offset) => {
        const slot = slots[placed.length + offset];

        return (
          <span key={stone.id} className="cairn-slot cairn-held" style={slotStyle(slot)}>
            <span className="cairn-stone" style={{ transform: `rotate(${slot.rotation}deg)` }}>
              <HeldStone mineral={stone.mineral} silhouette={stone.silhouette} />
            </span>
          </span>
        );
      })}
      {planned.map((_, offset) => {
        const index = firstGhost + offset,
          slot = slots[index];

        return (
          // Keyed by slot, so the ghost a stone fills goes and the ones above stay put.
          <span key={`ghost-${index}`} className="cairn-slot" style={slotStyle(slot)}>
            <span className="cairn-stone" style={{ transform: `rotate(${slot.rotation}deg)` }}>
              <GhostStone
                silhouette={slot.silhouette}
                className={index === firstGhost ? 'breathing' : undefined}
              />
            </span>
          </span>
        );
      })}
      {capstone && slots.length > 0 && (
        <>
          <FallingStone
            key={capstoneId}
            slot={slots[slots.length - 1]}
            fresh={capstoneFresh}
            delay={capstoneFresh ? 420 : 0}
          >
            <StoneMark mineral="amber" silhouette="f" />
          </FallingStone>
          <span
            className="cairn-sparkles"
            style={{
              left: slots[slots.length - 1].x + slots[slots.length - 1].width / 2,
              top: slots[slots.length - 1].y - 22 * scale,
            }}
          >
            <Sparkles scale={scale} delay={capstoneFresh ? 420 + drop.landing : 0} />
          </span>
        </>
      )}
    </div>
  );
}

/**
 * A stopping point, shown as a note in the owner's own hand: italic serif
 * under "Your marker", in the colour of stopping-point context. The session
 * panel, Welcome back and Set out all show it this way.
 */
export function MarkerNote({ text, when }: { text: string; when?: string }) {
  return (
    <div className="marker-note">
      <span className="marker-label">
        <Flag aria-hidden="true" />
        {when ? `Your marker · ${when}` : 'Your marker'}
      </span>
      <p>“{text}”</p>
    </div>
  );
}

/**
 * Completing a task by touching its pebble. The face answers the tap at once
 * and gives way to the saved state when it arrives; if nothing arrives within
 * three seconds it goes back to what it was, so a tap never feels ignored.
 */
export function PebbleButton({
  stone,
  done,
  dropped = false,
  title,
  onToggle,
}: {
  stone: CairnStone;
  done: boolean;
  dropped?: boolean;
  title: string;
  onToggle: () => void;
}) {
  const [promised, setPromised] = useState<boolean | null>(null);
  const token = useRef(0);
  const shown = promised ?? done;
  const face: PebbleFace = shown ? 'done' : dropped ? 'setDown' : 'open';

  // biome-ignore lint/correctness/useExhaustiveDependencies: the saved state arriving is what ends the promise
  useEffect(() => {
    setPromised(null);
  }, [done]);

  return (
    <button
      type="button"
      data-ui="specialized"
      className="pebble-button"
      aria-label={shown ? `Reopen “${title}”` : `Complete “${title}”`}
      title={shown ? 'Reopen' : 'Complete'}
      onClick={() => {
        const mine = ++token.current;

        setPromised(!done);

        setTimeout(() => {
          if (token.current === mine) {
            setPromised(null);
          }
        }, 3000);

        onToggle();
      }}
    >
      <span key={face} className="pebble-face">
        <PebbleMark mineral={stone.mineral} silhouette={stone.silhouette} face={face} />
      </span>
    </button>
  );
}
